import React, { useRef, useState, useMemo, useEffect } from 'react';
import { DataGrid, GridToolbar, useGridApiRef } from '@mui/x-data-grid';
import { Box, Button, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import BlockIcon from '@mui/icons-material/Block';
import LockIcon from '@mui/icons-material/Lock';
import { useTranslation } from 'react-i18next';

// Styled DataGrid component
const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
    width: '100%',
    '& .MuiDataGrid-columnHeaders': {
        // opacity: 0.7,
        backgroundColor: '#BFC8FF !important',
    },
    '& .MuiDataGrid-columnHeader': {
        backgroundColor: '#BFC8FF !important',
    },
    '& .MuiDataGrid-columnHeaderTitle': {
        color: 'var(--Foundation-Blue-Normal, #304FFF)',
        textAlign: 'center',
        fontFamily: 'Montserrat',
        fontSize: '14px',
        fontWeight: 600,
        lineHeight: 'normal',
    },
    '& .MuiDataGrid-row': {
        borderTop: '1px solid rgba(191, 191, 193, 0.56)',
        backgroundColor: 'rgba(234, 237, 255, 0.32)',
    },
    '& .MuiDataGrid-cell': {
        whiteSpace: 'normal',
        wordWrap: 'break-word',
    },
    '& .MuiDataGrid-cell:focus': {
        outline: 'none',
    },
    '& .disableUser': {
        pointerEvents: 'none',
        opacity: 0.5,
    },
}));

const SubAccountantsDataGrid = ({
    filteredSubAccountants,
    handleViewButtonClick,
    showHourlyRate = true, // Flag to control hourly rate column visibility
    allSubAccountantsPermissions = {}, // Permissions map for all subAccountants
    allSubAccountantsRestrictions = {}, // Restriction keys per subAccountant (global scope)
    onViewRestrictions, // (row) => void - open modal to view restrictions by client
}) => {

    const { i18n, t } = useTranslation();
    const [selectedLanguage, setSelectedLanguage] = useState(localStorage.getItem('language') || 'he');

    // const initChatConf = async () => {
    //   await ChatService.InitChat();

    // }
    useEffect(() => {
        // Check for saved language in localStorage
        const currentLanguage = localStorage.getItem('language') || 'he'; // Default to 'en' if no language is stored

        i18n.changeLanguage(currentLanguage);
        // initChatConf();
    }, []);

    useEffect(() => {
        const currentLanguage = localStorage.getItem('language') || 'he';
        setSelectedLanguage(currentLanguage);
    }, [i18n.language]);
  
    const columns = [
        {
            field: 'subAccountantId',
            headerName: t('SubAccountantId'),
            headerAlign: 'center',
            align: 'center',
            flex: 1,
        },
        {
            field: 'name',
            headerName: t('name'),
            headerAlign: 'center',
            align: 'center',
            flex: 1,
        },
        {
            field: 'phoneNumber',
            headerName: t('phoneNumber'),
            headerAlign: 'center',
            align: 'center',
            flex: 1,
            
        },
        {
            field: 'email',
            headerName: t('email'),
            headerAlign: 'center',
            align: 'center',
            flex: 1,
        },
        ...(showHourlyRate ? [{
            field: 'pricePerHour',
            headerName: t('HourlyCost') || 'Hourly Cost',
            headerAlign: 'center',
            align: 'center',
            flex: 1,
            renderCell: (params) => (
                <span style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    gap: '4px'
                }}>
                    {params.value ? (
                        <>
                            <span>₪</span>
                            <span>{params.value.toFixed(2)}</span>
                        </>
                    ) : (
                        <span style={{ color: '#999', fontStyle: 'italic' }}>
                            {t('notSet') || 'Not set'}
                        </span>
                    )}
                </span>
            ),
        }] : []),
        {
            field: 'permissions',
            headerName: t('Permissions'),
            headerAlign: 'center',
            align: 'center',
            flex: 1.5,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const subAccountantId = params.row.subAccountantId != null ? String(params.row.subAccountantId) : '';
                const permissions = allSubAccountantsPermissions[subAccountantId] || [];
                
                if (permissions.length === 0) {
                    return (
                        <span style={{ color: '#999', fontStyle: 'italic', fontSize: '12px' }}>
                            {t('NoRestrictions') || 'No restrictions'}
                        </span>
                    );
                }
                
                return (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                        {permissions.map((perm) => (
                            <Chip
                                key={perm.permissionId}
                                label={t(perm.permission.name) || perm.permission.name}
                                size="small"
                                icon={<BlockIcon />}
                                color="warning"
                                sx={{ 
                                    fontSize: '11px',
                                    height: '24px',
                                    '& .MuiChip-icon': {
                                        fontSize: '14px'
                                    }
                                }}
                            />
                        ))}
                    </Box>
                );
            },
        },
        ...(onViewRestrictions
            ? [{
                field: 'viewRestrictions',
                headerName: t('ViewRestrictions') || 'View restrictions',
                headerAlign: 'center',
                align: 'center',
                width: 100,
                flex: 0.8,
                sortable: false,
                filterable: false,
                renderCell: (params) => {
                    const subAccountantId = params.row.subAccountantId != null ? String(params.row.subAccountantId) : '';
                    const keys = allSubAccountantsRestrictions[subAccountantId] || [];
                    const hasRestrictions = keys.length > 0;
                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            {hasRestrictions && (
                                <LockIcon sx={{ fontSize: 16, color: 'warning.main' }} titleAccess={t('HasRestrictions') || 'Has restrictions'} />
                            )}
                            <Button
                                variant={hasRestrictions ? 'contained' : 'outlined'}
                                color={hasRestrictions ? 'warning' : 'inherit'}
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onViewRestrictions(params.row);
                                }}
                                style={{ minWidth: '70px', padding: '4px 8px' }}
                            >
                                {t('view')}
                                {hasRestrictions && ` (${keys.length})`}
                            </Button>
                        </Box>
                    );
                },
            }]
            : []),
        {
            field: 'view',
            headerName: t('view'),
            headerAlign: 'center',
            align: 'center',
            width: 80, // Fixed width for the 'View' column
            flex: 1,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => handleViewButtonClick(params.row)}
                    style={{ minWidth: '60px', padding: '4px 8px' }}
                >
                    {t('view')}
                </Button>
            ),
        },
    ];


    const apiRef = useGridApiRef();
    const [selectedRows, setSelectedRows] = useState([]);

    // Handlers for sorting and filtering
    const handleSortModelChange = (model) => {
        // Implement your sort logic here
    };

    const handleFilterModelChange = (model) => {
        // Implement your filter logic here
    };

    const handleEdit = (params) => {
        // Implement your edit logic here
    };

    return (
        <Box
            sx={{
                height: '50vh',
                width: '100%',
                boxSizing: 'border-box',
                backgroundColor: '#fff',
                direction: 'ltr',
            }}
        >

            <StyledDataGrid
                apiRef={apiRef}
                rows={filteredSubAccountants}
                columns={columns}
                pageSize={25} // Max rows set to 25
                rowsPerPageOptions={[10, 25, 50]}
                disableRowSelectionOnClick
                onSelectionModelChange={(ids) => setSelectedRows(ids)}
                getRowClassName={(params) => params.row.rowClassName || ''}
                getRowId={(row) => row.subAccountantId}
                onCellEditCommit={handleEdit}
                selectionModel={selectedRows}
                components={{ Toolbar: GridToolbar }}
                componentsProps={{
                    toolbar: {
                        showQuickFilter: true,
                    },
                  
                }}
                pagination
                onSortModelChange={handleSortModelChange}
                onFilterModelChange={handleFilterModelChange}
            />

        </Box>
    );
};

export default SubAccountantsDataGrid;
